import { range } from '@basementuniverse/utils';
import {
  faClipboard,
  faClipboardCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { SingleValue } from 'react-select';
import classNames from '../utilities/class-names';
import './CodeViewer.scss';
import { Select, SelectOptionProps } from './Select';

const COPY_TO_CLIPBOARD_RESET_DELAY = 3000;

/**
 * A tab of code, laid out with real whitespace, with its plain text for
 * copying. <Snippet> renders these from a generated snippet
 */
export type CodeViewerVariant = {
  label: string;
  lang?: string;
  content: ReactNode;
  text: string;
  lineCount: number;
};

type CodeViewerProps = {
  className?: string;
  copyable?: boolean;
  variants: CodeViewerVariant[];
  border?: boolean;
  lineNumbers?: boolean;
  [key: string]: any;
};

export function CodeViewer({
  className,
  copyable = true,
  variants,
  border = false,
  lineNumbers = true,
  ...props
}: CodeViewerProps): ReactElement {
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    variants[0]?.label ?? ''
  );
  const [textCopiedToClipboard, setTextCopiedToClipboard] = useState(false);

  const selectedVariant = variants.find(
    variant => variant.label === selectedLanguage
  );

  const languageOptions: SelectOptionProps<string>[] = useMemo(
    () =>
      variants.map(variant => ({
        label: variant.label,
        value: variant.label,
      })),
    [variants]
  );

  const handleChangeSelectedLanguage = useCallback(
    async (selectedLanguage: SingleValue<SelectOptionProps<string>>) => {
      if (selectedLanguage) {
        setSelectedLanguage(selectedLanguage.value);
      }
    },
    [setSelectedLanguage]
  );

  useEffect(() => {
    if (textCopiedToClipboard) {
      setTimeout(() => {
        setTextCopiedToClipboard(false);
      }, COPY_TO_CLIPBOARD_RESET_DELAY);
    }
  }, [textCopiedToClipboard]);

  return (
    <div
      className={classNames(
        'code-viewer',
        className,
        border ? 'border' : undefined,
        lineNumbers ? 'line-numbers' : undefined
      )}
      {...props}
    >
      <div className="code-viewer-header">
        <Select
          name="language-select"
          className="code-viewer-language-select"
          options={languageOptions}
          defaultValue={languageOptions.find(
            option => option.value === selectedLanguage
          )}
          onChange={handleChangeSelectedLanguage as any}
          disabled={languageOptions.length <= 1}
        />
        {copyable && (
          <CopyToClipboard
            text={selectedVariant?.text ?? ''}
            onCopy={() => {
              setTextCopiedToClipboard(true);
            }}
          >
            <button
              type="button"
              className="code-viewer-copy-to-clipboard-button"
            >
              <FontAwesomeIcon
                icon={textCopiedToClipboard ? faClipboardCheck : faClipboard}
              />
            </button>
          </CopyToClipboard>
        )}
      </div>
      {lineNumbers && (
        <span className="code-viewer-line-numbers">
          {range(selectedVariant?.lineCount ?? 0).map(i => (
            <span key={i}>{i + 1}</span>
          ))}
        </span>
      )}
      <code className="code-viewer-code" data-language={selectedVariant?.lang}>
        {selectedVariant?.content}
      </code>
    </div>
  );
}
